import type { PostTryOnJourney } from '../model/post-tryon'
import { getWhatsAppAppUrl, isSafeWhatsAppNavigationUrl } from '../return/whatsapp-return-target'

export type PostTryOnCompletionRecord = {
  version: 1
  experienceId: string
  journeyId: string
  productType: string
  returnUrl?: string
  checkoutStatus: string
  completedAt: string
  expiresAt: string
  lastReturnAttemptAt?: string
}

type LocationInput = Pick<Location, 'pathname' | 'search'>
type StorageInput = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const recordPrefix = 'mia:post-tryon:completion:'
const activeRecordKey = 'mia:post-tryon:completion:active'
const defaultLifetimeMs = 24 * 60 * 60 * 1_000

function storageSafe(): StorageInput | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

function fingerprint(value: string) {
  let first = 2166136261
  let second = 2246822519
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    first = Math.imul(first ^ code, 16777619)
    second = Math.imul(second ^ code, 3266489917)
  }
  return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`
}

function completionExpiry(journey: PostTryOnJourney) {
  const journeyExpiry = new Date(journey.expiresAt).getTime()
  const fallback = Date.now() + defaultLifetimeMs
  if (!Number.isFinite(journeyExpiry) || journeyExpiry <= Date.now()) return new Date(fallback).toISOString()
  return new Date(Math.min(journeyExpiry, fallback)).toISOString()
}

function parseRecord(raw: string | null): PostTryOnCompletionRecord | undefined {
  if (!raw) return undefined
  try {
    const record = JSON.parse(raw) as PostTryOnCompletionRecord
    if (record.version !== 1 || !record.experienceId || !record.journeyId || !record.completedAt || !record.expiresAt) return undefined
    if (new Date(record.expiresAt).getTime() <= Date.now()) return undefined
    if (record.returnUrl && !isSafeWhatsAppNavigationUrl(record.returnUrl)) return { ...record, returnUrl: undefined }
    return record
  } catch {
    return undefined
  }
}

export function postTryOnExperienceId(location: LocationInput) {
  const params = new URLSearchParams(location.search)
  const context = params.get('context')?.trim() || ''
  const publicKey = params.get('key')?.trim() || ''
  const discriminator = context ? `context:${context}` : publicKey ? `key:${publicKey}` : 'direct'
  return `${location.pathname.replace(/\/+$/, '') || '/'}:${fingerprint(discriminator)}`
}

function recordKey(experienceId: string) {
  return `${recordPrefix}${experienceId}`
}

export function readPostTryOnCompletion(location: LocationInput, storage: StorageInput | undefined = storageSafe()) {
  if (!storage) return undefined
  const experienceId = postTryOnExperienceId(location)
  const key = recordKey(experienceId)
  const record = parseRecord(storage.getItem(key))
  if (!record) storage.removeItem(key)
  return record
}

export function readActivePostTryOnCompletion(storage: StorageInput | undefined = storageSafe()) {
  if (!storage) return undefined
  const record = parseRecord(storage.getItem(activeRecordKey))
  if (!record) storage.removeItem(activeRecordKey)
  return record
}

export function markPostTryOnSessionCompleted(journey: PostTryOnJourney, location: LocationInput = window.location, storage: StorageInput | undefined = storageSafe()) {
  const experienceId = postTryOnExperienceId(location)
  const returnUrl = getWhatsAppAppUrl(journey)
  const record: PostTryOnCompletionRecord = {
    version: 1,
    experienceId,
    journeyId: journey.id,
    productType: journey.productType,
    returnUrl: isSafeWhatsAppNavigationUrl(returnUrl) ? returnUrl : undefined,
    checkoutStatus: journey.checkoutStatus,
    completedAt: new Date().toISOString(),
    expiresAt: completionExpiry(journey),
  }
  storage?.setItem(recordKey(experienceId), JSON.stringify(record))
  storage?.setItem(activeRecordKey, JSON.stringify(record))
  return record
}

export function registerPostTryOnReturnAttempt(record: PostTryOnCompletionRecord, storage: StorageInput | undefined = storageSafe()) {
  const updated = { ...record, lastReturnAttemptAt: new Date().toISOString() }
  storage?.setItem(recordKey(record.experienceId), JSON.stringify(updated))
  storage?.setItem(activeRecordKey, JSON.stringify(updated))
  return updated
}

export function clearPostTryOnCompletion(location: LocationInput, storage: StorageInput | undefined = storageSafe()) {
  if (!storage) return
  const experienceId = postTryOnExperienceId(location)
  storage.removeItem(recordKey(experienceId))
  const active = readActivePostTryOnCompletion(storage)
  if (active?.experienceId === experienceId) storage.removeItem(activeRecordKey)
}
